import axios from 'axios';
import React, { useRef, useState } from 'react';
import { toast } from 'react-hot-toast';
import Latex from '../../../../../apps/frontend/src/components/global/Latex';
import { QUESTION_TYPE } from '../../utils/Question_Type';
import { StarIcon } from '@phosphor-icons/react';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import BookmarkController from './BookmarkController';

const statusStyles = {
  wasted: { color: 'var(--q3-danger-normal)', borderColor: 'var(--q3-danger-light)' },
  confused: { color: 'var(--q3-neutral-default)', borderColor: 'var(--q3-stroke-normal)' },
  overtime: { color: 'var(--q3-yellow-normal)', borderColor: 'var(--q3-yellow-light)' },
  perfect: { color: 'var(--q3-success-normal)', borderColor: 'var(--q3-success-light)' },
  default: { color: 'var(--q3-neutral-default)', borderColor: 'var(--q3-neutral-default)' },
  'not tagged': { color: 'var(--q3-neutral-default)', borderColor: 'var(--q3-neutral-default)' },
};

const getStatusStyles = (status) => statusStyles[status?.toLowerCase()] || statusStyles.default;

const QuestionListItem = ({ question, index, currentPage, onClick, displayTitle, packId, testId }) => {
  const [markedAsImp, setMarkedAsImp] = useState(question.isImportant);

  const textRef = useRef(null);

  const questionNumber = index + 1 + (currentPage - 1) * 10;
  const tagDetail = question?.user_question_tags?.tags_detail;

  const handleMarkAsImp = async () => {
    try {
      await axios.patch(`/api/v1/answers/${question._id}/importance`);
      const alreadyMarkedImp = markedAsImp;
      setMarkedAsImp(!markedAsImp);
      if (alreadyMarkedImp) {
        toast.success('Question removed from important', {
          position: 'top-center',
          duration: 800,
        });
      } else {
        toast.success('Question marked as important', {
          position: 'top-center',
          duration: 800,
        });
      }
    } catch (error) {
      toast.error('Something went wrong', {
        position: 'top-center',
        duration: 800,
      });
    }
  };

  return (
    <div
      onClick={onClick}
      className="border-b px-5 cursor-pointer py-2 hover:bg-[var(--q3-surface-dim)]"
      key={question._id}
    >
      <div className="flex flex-row items-start sm:items-center gap-4 py-2">
        <div className="font-medium text-sm text-[var(--q3-neutral-default)]">{`Q${questionNumber}`}</div>

        <div className="flex-1 flex flex-col items-start gap-3">
          <div className="flex items-center gap-3 flex-wrap">
            {question?.attemptOverview && (
              <Badge
                variant="outline"
                className="text-xs rounded-sm font-medium border-2"
                style={getStatusStyles(question.attemptOverview)}
              >
                {question.attemptOverview?.toUpperCase()}
              </Badge>
            )}
            {tagDetail?._id && (
              <Badge key={tagDetail._id} variant="outline" className="text-xs flex items-center gap-1 border-2">
                {tagDetail.icon && <img src={tagDetail.icon} alt={tagDetail.title} className="h-auto w-4" />}
                {tagDetail.title?.toUpperCase()}
              </Badge>
            )}
            <span className="text-xs text-[var(--q3-neutral-light)]">
              {displayTitle} • {QUESTION_TYPE[question.type]}
            </span>
          </div>

          {/* Truncated Preview */}
          <div
            ref={textRef}
            className="text-[var(--q3-neutral-default)] text-sm max-h-[108px] overflow-hidden image-invert-wrapper"
          >
            {question?.question?.question?.text ? (
              <Latex>{question.question.question.text}</Latex>
            ) : (
              <span>No question text available.</span>
            )}
          </div>
        </div>

        <div className="self-start sm:self-center sm:ml-auto sm:pl-4">
          <div onClick={(e) => e.stopPropagation()}>
            <Button
              variant="ghost"
              className={`flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[var(--q3-neutral-default)] border hover:bg-[var(--q3-neutral-default)]/50} hover:scale-110`}
              onClick={handleMarkAsImp}
            >
              <StarIcon className="text-[var(--q3-yellow-normal)]" weight={markedAsImp ? 'fill' : 'regular'} />
              {!markedAsImp && <p className="md:block hidden">Mark Imp</p>}
            </Button>
          </div>
        </div>
        <div className="self-start sm:self-center">
          <BookmarkController questionId={question.question._id} packId={packId} testId={testId} />
        </div>
      </div>

      {question.notes && (
        <div className="text-[var(--q3-neutral-light)] text-sm pl-0 md:pl-8">
          <strong className="text-[var(--q3-neutral-default)]">Notes:</strong> {question.notes}
        </div>
      )}

      {question.hint && <p className="text-[var(--q3-neutral-light)] text-sm pl-12 pb-2">{question.hint}</p>}
    </div>
  );
};

export default QuestionListItem;
